/// <amd-module name="Wit/WorkItemForm/History/UpdateDetails" />
import * as React from "react";
import { PersonaDetailsProvider } from "VSS/Features/Identities/PersonaDetailsProvider";
import { usePageContext } from "VSS/Platform/Layout";
import { Persona, PersonaSize } from "VSSUI/Persona";
import { IVssPageContext } from "VSS/Platform/Context";
import { localeDateFormat } from "VSS/Platform/Date";
import { Time } from "VSSUI/Time";
import { tooltipString } from "VSSUI/Utilities/Date";
import { getExtendedTimeFormat } from "Wit/Common/DateUtils";
import { IHistoryItem } from "../Common/Services";
import { UpdateDiffs } from "./Diffs/UpdateDiffs";
import { getAuthorizedAsIdentity, getUpdateDate, hasSameNameWithRevisedBy } from "./HistoryUtils";
import { UpdateSummary } from "./UpdateSummary";
import * as Resources from "Wit/WorkItemForm/Resources";
import { FormatComponent } from "VSS/Platform/Components/Format/Format";

const shortFormat = new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric"
});

export interface IUpdateDetailsProps {
    update: IHistoryItem;
}

export function UpdateDetails({ update }: IUpdateDetailsProps) {
    return (
        <div className="update-details flex-column">
            <UpdateHeader update={update} />
            <UpdateDiffs update={update} />
        </div>
    );
}

export interface IUpdateHeaderProps {
    update: IHistoryItem;
}

function UpdateHeader({ update }: IUpdateHeaderProps) {
    const pageContext = usePageContext();
    return (
        <div className="update-details-header flex-row">
            <Persona
                className="margin-8"
                identity={update.revisedBy}
                personaProvider={new PersonaDetailsProvider(pageContext)}
                size={PersonaSize.size40}
            />
            <span className="flex-column justify-center flex-grow">
                <div className="flex-row justify-space-between">
                    {renderAuthorName(update, pageContext)}
                    <Time
                        className={"update-date font-size"}
                        dateTime={getUpdateDate(update)}
                        ariaLabel={tooltipString(getUpdateDate(update))}
                        getNextInterval={() => {
                            return -1;
                        }}
                        getTimeString={() => {
                            return localeDateFormat(getUpdateDate(update), getExtendedTimeFormat(), shortFormat);
                        }}
                        tooltipProps={{
                            renderContent: () => tooltipString(getUpdateDate(update))
                        }}
                    />
                </div>
                <div className="flex-row justify-space-between">
                    <UpdateSummary update={update} />
                </div>
            </span>
        </div>
    );
}

function renderAuthorName(update: IHistoryItem, pageContext: IVssPageContext): JSX.Element {
    const authorizedAsIdentity = getAuthorizedAsIdentity(update, pageContext);
    const haveSameName = hasSameNameWithRevisedBy(update, authorizedAsIdentity);

    const reviserBySpan = (
        <span className="font-weight-heavy" key={`revisedBy-${update.id}`}>
            {update.revisedBy.displayName}
        </span>
    );

    if (!authorizedAsIdentity || haveSameName) {
        return reviserBySpan;
    }

    const authorizedAsSpan = (
        <span className="font-weight-heavy" key={`authorizedA-${update.id}`}>
            {authorizedAsIdentity.displayName}
        </span>
    );

    return <FormatComponent format={Resources.HistoryControlViaTemplate}>{[reviserBySpan, authorizedAsSpan]}</FormatComponent>;
}
